import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CabifyLate } from './CabifyLate';
import { CabifyControl } from './CabifyControl';
import { X, Truck, ExternalLink, Printer, Loader2, Send, Copy, Check } from 'lucide-react';
import { supabase } from './supabase';
import { isOwnShipping } from './shipping';
import { AddressField } from './ui/AddressField';
import { verifyAddress, ensureOrderCoords, AddressCheck } from './geocode';

// MÓDULO ENCAPSULADO — Cabify Logistics (batch). Lista los pedidos EMPAQUETADOS
// calificados para Cabify, crea los envíos (sin despachar), baja TODAS las
// etiquetas en un solo PDF para imprimir/pegar, y despacha todo junto.
// Para eliminar el módulo: borrar este archivo + sus usos en App.tsx (buscar 'MÓDULO CABIFY'),
// la edge function cabify-logistics y la tabla cabify_deliveries.

export interface CabifyBatchOrder {
  id: number;
  displayId: string;
  delivery_lat?: number | null;
  delivery_lon?: number | null;
  contact_name?: string;
  contact_phone?: string;
  delivery_address?: string;
  delivery_unit?: string | null;
}

// Califica para Cabify: el envío es nuestro (ver src/shipping.ts — una sola definición,
// porque la regla duplicada ya causó dos bugs: órdenes manuales sin botón de Cabify y
// sin WhatsApp de despacho).
export function cabifyQualifies(o: { delivery_type?: string; source?: string; shipping_option?: string }): boolean {
  return isOwnShipping(o);
}

interface DeliveryRow { order_id: number; parcel_id: string; status: string; raw: any; }

// Estados terminales fallidos: el envío murió (devuelto/cancelado/falló el retiro).
// Se tratan como "sin envío": la card vuelve a ofrecer Generar etiqueta (re-despacho).
// El historial completo queda visible en el popup de estado.
export const CABIFY_DEAD = ['deleted', 'returned', 'returnedtoorigin', 'requestercancel', 'internalcancel', 'pickupfailed'];

// Traducción de estados de Cabify (los actualiza cabify-webhook en vivo).
export const CABIFY_STATE_ES: Record<string, string> = {
  created: 'Etiqueta creada (sin despachar)',
  shipped: 'Despachado — esperando asignación',
  ready: 'Etiqueta lista (sin despachar)',
  readytopickup: 'Despachado — esperando repartidor',
  onroutetofinalhub: 'Rumbo al centro de distribución 🚚',
  receivedonfinalhub: 'En el centro de distribución — llega hoy',
  readytodispatch: 'En el centro — listo para salir',
  onroutetodelivery: 'En camino al cliente 🛵',
  undelivered: 'No se pudo entregar ⚠️ (Cabify reintenta)',
  onroutetoreturn: 'Volviendo al local ⚠️',
  returnedtoorigin: 'Devuelto al local',
  qualifiedforpickup: 'Buscando repartidor',
  onroutetopickup: 'Repartidor yendo al local 🛵',
  pickingup: 'Retirando el paquete',
  intransit: 'En camino al cliente 🛵',
  delivering: 'Entregando',
  delivered: 'Entregado ✅',
  returning: 'Volviendo al local ⚠️',
  returned: 'Devuelto al local',
  incident: 'Incidencia ⚠️',
  requestercancel: 'Cancelado por nosotros',
  internalcancel: 'Cancelado por Cabify',
  pickupfailed: 'Falló el retiro ⚠️',
  deleted: 'Eliminado',
};

// Popup interno con el estado del envío Cabify de un pedido: estado actual
// (refrescado contra la API), historial de eventos del webhook y link de seguimiento.
function CopyIdButton({ id }: { id: string }) {
  const [ok, setOk] = useState(false);
  return (
    <button type="button" title="Copiar el ID de Cabify"
      onClick={() => { navigator.clipboard?.writeText(id).then(() => { setOk(true); setTimeout(() => setOk(false), 1400); }); }}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 6, fontSize: '0.72rem', fontWeight: 600, cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--bg)', color: ok ? '#10b981' : 'var(--text)' }}>
      {ok ? <Check size={12} /> : <Copy size={12} />} {ok ? 'Copiado' : 'Copiar'}
    </button>
  );
}

export function CabifyStatusModal({ orderId, displayId, onClose }: { orderId: number; displayId: string; onClose: () => void }) {
  // Una sección POR ETIQUETA (02/11/2026): un pedido en varias cajas tiene varias etiquetas que viajan
  // por separado, cada una con su estado, su link de seguimiento y su historial. Antes se veía solo la
  // última. Las etiquetas muertas (borradas/canceladas/devueltas) van al final, atenuadas.
  const [rows, setRows] = useState<any[]>([]);
  const [refreshing, setRefreshing] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    const { data } = await supabase.from('cabify_deliveries')
      .select('*').eq('order_id', orderId).order('created_at', { ascending: true });
    const list = (data || []).sort((a: any, b: any) =>
      Number(CABIFY_DEAD.includes(a.status)) - Number(CABIFY_DEAD.includes(b.status))
      || String(a.external_id || '').localeCompare(String(b.external_id || ''), 'es', { numeric: true }));
    setRows(list);
    setLoaded(true);
    return list;
  }, [orderId]);

  const refresh = useCallback(async (parcelIds?: string[]) => {
    setRefreshing(parcelIds?.length === 1 ? parcelIds[0] : 'all');
    try {
      const ids = parcelIds || (await load()).filter((r: any) => !CABIFY_DEAD.includes(r.status)).map((r: any) => r.parcel_id);
      await Promise.all(ids.map((id: string) => supabase.functions.invoke('cabify-logistics', { body: { action: 'status', parcel_id: id } })));
      await load();
    } finally {
      setRefreshing(null);
    }
  }, [load]);

  useEffect(() => { refresh(); }, [refresh]);

  const live = rows.filter(r => !CABIFY_DEAD.includes(r.status));
  const label = (r: any) => (String(r.external_id || '').match(/\d+\/\d+/) || [''])[0];

  const ov: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
  const card: React.CSSProperties = { background: 'var(--card)', borderRadius: 16, width: '100%', maxWidth: 460, padding: 20, maxHeight: '85vh', overflowY: 'auto', color: 'var(--text)' };
  const small: React.CSSProperties = { fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' };

  return (
    <div style={ov} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: '1.05rem' }}>
            <Truck size={18} color="#7c3aed" /> Cabify · {displayId}{live.length > 1 ? ` · ${live.length} etiquetas` : ''}
          </h3>
          <button style={{ border: 'none', background: 'transparent', padding: 6, cursor: 'pointer', color: 'var(--text)' }} onClick={onClose}><X size={20} /></button>
        </div>
        {live.length > 1 && (
          <p style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', margin: '0 0 12px' }}>
            El pedido va en {live.length} cajas y cada una viaja por separado: cada etiqueta tiene su estado y su seguimiento.
          </p>
        )}

        {!loaded ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', padding: '12px 0' }}>
            <Loader2 size={16} className="spin" /> Cargando…
          </div>
        ) : !rows.length ? (
          <div style={{ color: 'var(--text-muted)', padding: '12px 0' }}>Este pedido no tiene envíos Cabify.</div>
        ) : (
          <>
            {rows.map((d: any) => {
              const dead = CABIFY_DEAD.includes(d.status);
              const tracking = d?.raw?.shipped?.parcels?.[0]?.tracking_url || d?.raw?.tracking_url || null;
              const events: any[] = Array.isArray(d?.raw?.events) ? [...d.raw.events].reverse() : [];
              return (
                <div key={d.id} style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 14, marginBottom: 12, opacity: dead ? 0.55 : 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                    <div style={small}>{label(d) ? `Etiqueta ${label(d)}` : 'Etiqueta'}{dead ? ' · anulada' : ''}</div>
                  </div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 800, marginTop: 2, color: d.status === 'delivered' ? '#10b981' : undefined }}>{CABIFY_STATE_ES[d.status] || d.status}</div>
                  {/* ID completo de Cabify (para consultar a soporte), copiable — 03/11/2026 */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>ID Cabify:</span>
                    <code style={{ fontSize: '0.74rem', fontFamily: 'monospace', userSelect: 'all', wordBreak: 'break-all', color: 'var(--text)' }}>{d.parcel_id}</code>
                    <CopyIdButton id={d.parcel_id} />
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginTop: 8 }}>
                    {tracking && !dead && (
                      <a href={tracking} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#7c3aed', fontWeight: 700, fontSize: '0.85rem' }}>
                        Seguimiento {label(d)} <ExternalLink size={13} />
                      </a>
                    )}
                    {!dead && (
                      <button onClick={() => refresh([d.parcel_id])} disabled={!!refreshing}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 8, fontWeight: 600, fontSize: '0.78rem', cursor: refreshing ? 'wait' : 'pointer', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }}>
                        {refreshing === d.parcel_id || refreshing === 'all' ? <Loader2 size={12} className="spin" /> : <Truck size={12} />} Refrescar
                      </button>
                    )}
                  </div>
                  {events.length > 0 && (
                    <details style={{ marginTop: 10 }} open={rows.length === 1}>
                      <summary style={{ ...small, cursor: 'pointer' }}>Historial ({events.length})</summary>
                      {events.map((ev, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', padding: '6px 0', borderTop: '1px solid var(--border)' }}>
                          <span>{CABIFY_STATE_ES[ev.state] || ev.state}</span>
                          <span style={{ color: 'var(--text-muted)' }}>{new Date(ev.at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                      ))}
                    </details>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

// Lista de TODOS los envíos Cabify con su estado en vivo, filtrable por estado.
function CabifyShipments({ onOpenStatus }: { onOpenStatus: (orderId: number, displayId: string) => void }) {
  const [rows, setRows] = useState<any[]>([]);
  const [orderInfo, setOrderInfo] = useState<Map<number, { display: string; name: string }>>(new Map());
  const [filter, setFilter] = useState<string>('activos');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [waBusy, setWaBusy] = useState<string | null>(null);
  const [lblBusy, setLblBusy] = useState<string | null>(null);

  // Abre la etiqueta de UN envío en una pestaña nueva. Antes la impresión solo existía
  // en el flujo de despacho masivo: si una etiqueta se generaba aparte (o se agregaba una
  // extra por más cajas), acá se veía pero no había forma de imprimirla.
  // Imprime TODAS las etiquetas de un pedido, no solo la de la fila clickeada.
  // Un pedido multi-bulto tiene una fila por bulto (ej "#4515 1/2" y "#4515 2/2"), y con
  // el botón imprimiendo una sola era fácil salir con media etiquetería (caso #4515, 30/09:
  // las 2 estaban generadas y se imprimió 1).
  const printOne = async (parcelIds: string | string[], busyKey?: string) => {
    const ids = Array.isArray(parcelIds) ? parcelIds : [parcelIds];
    setLblBusy(busyKey || ids[0]);
    try {
      const { data, error } = await supabase.functions.invoke('cabify-logistics', {
        body: { action: 'labels', parcel_ids: ids },
      });
      if (error || !data?.ok || !data?.pdf_base64) {
        window.alert(`No se pudo generar la etiqueta: ${data?.error || error?.message || 'error'}`);
        return;
      }
      const bin = atob(data.pdf_base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } finally {
      setLblBusy(null);
    }
  };
  const [sortBy, setSortBy] = useState<'fecha' | 'estado' | 'pedido'>('fecha');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('cabify_deliveries')
      .select('id, order_id, external_id, parcel_id, status, raw, created_at')
      .neq('status', 'deleted')
      .not('parcel_id', 'like', 'demo%')
      .order('created_at', { ascending: false })
      .limit(200);
    const list = data || [];
    setRows(list);
    const ids = Array.from(new Set(list.map((r: any) => r.order_id).filter(Boolean)));
    if (ids.length) {
      const { data: ords } = await supabase.from('orders')
        .select('id, tiendanube_id, source, contact_name').in('id', ids);
      const m = new Map<number, { display: string; name: string }>();
      for (const o of (ords || []) as any[]) {
        const display = o.source === 'woocommerce' && o.tiendanube_id ? `BT-${String(o.tiendanube_id).replace(/^(WC-|BT-)/i, '')}`
          : (o.source === 'tiendanube' || o.source === 'mercadolibre') && o.tiendanube_id ? `#${o.tiendanube_id}`
          : `K-${o.id}`;
        m.set(o.id, { display, name: o.contact_name || '' });
      }
      setOrderInfo(m);
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const syncStates = async () => {
    setSyncing(true);
    try {
      await supabase.functions.invoke('cabify-logistics', { body: { action: 'sync_states' } });
      await load();
    } finally {
      setSyncing(false);
    }
  };

  const ACTIVE = ['created', 'shipped', 'ready', 'readytopickup', 'qualifiedforpickup', 'onroutetopickup', 'pickingup', 'onroutetofinalhub', 'receivedonfinalhub', 'readytodispatch', 'onroutetodelivery', 'intransit', 'delivering', 'undelivered', 'onroutetoreturn', 'returning', 'incident'];
  const STATE_ORDER = ['created', 'ready', 'shipped', 'readytopickup', 'qualifiedforpickup', 'onroutetopickup', 'pickingup', 'onroutetofinalhub', 'receivedonfinalhub', 'readytodispatch', 'qualifiedforpickup', 'intransit', 'onroutetodelivery', 'delivering', 'delivered', 'undelivered', 'onroutetoreturn', 'returning', 'returned', 'returnedtoorigin', 'incident', 'pickupfailed', 'requestercancel', 'internalcancel'];
  const counts = useMemo(() => {
    const c = new Map<string, number>();
    for (const r of rows) c.set(r.status, (c.get(r.status) || 0) + 1);
    return c;
  }, [rows]);
  const filtered = useMemo(() => {
    let list = filter === 'todos' ? rows
      : filter === 'activos' ? rows.filter(r => ACTIVE.includes(r.status))
      : rows.filter(r => r.status === filter);
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter(r => {
        const info = orderInfo.get(r.order_id);
        return String(r.external_id || '').toLowerCase().includes(term)
          || (info?.display || '').toLowerCase().includes(term)
          || (info?.name || '').toLowerCase().includes(term);
      });
    }
    const byDate = (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    if (sortBy === 'estado') {
      list = [...list].sort((a, b) => (STATE_ORDER.indexOf(a.status) - STATE_ORDER.indexOf(b.status)) || byDate(a, b));
    } else if (sortBy === 'pedido') {
      list = [...list].sort((a, b) => String(orderInfo.get(b.order_id)?.display || b.external_id).localeCompare(String(orderInfo.get(a.order_id)?.display || a.external_id), 'es', { numeric: true }));
    } else {
      list = [...list].sort(byDate);
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, filter, search, sortBy, orderInfo]);

  const chipBtn = (key: string, label: string, n?: number): React.ReactNode => (
    <button key={key} onClick={() => setFilter(key)}
      style={{ padding: '4px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', border: '1px solid var(--border)', background: filter === key ? '#7c3aed' : 'var(--bg)', color: filter === key ? '#fff' : 'var(--text)' }}>
      {label}{n != null ? ` (${n})` : ''}
    </button>
  );

  const stateColor = (st: string) => st === 'delivered' ? '#10b981'
    : ['returned', 'returnedtoorigin', 'requestercancel', 'internalcancel', 'pickupfailed', 'incident', 'returning', 'undelivered', 'onroutetoreturn'].includes(st) ? '#dc2626'
    : ['intransit', 'delivering', 'onroutetopickup', 'pickingup', 'onroutetofinalhub', 'receivedonfinalhub'].includes(st) ? '#7c3aed'
    : 'var(--text-secondary)';

  return (
    <>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        {chipBtn('activos', 'Activos', rows.filter(r => ACTIVE.includes(r.status)).length)}
        {chipBtn('todos', 'Todos', rows.length)}
        {Array.from(counts.entries()).sort((a, b) => b[1] - a[1]).map(([st, n]) => chipBtn(st, CABIFY_STATE_ES[st] || st, n))}
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar pedido / cliente…"
            style={{ padding: '5px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '0.78rem', width: 170 }} />
          <select value={sortBy} onChange={e => setSortBy(e.target.value as any)}
            style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '0.78rem' }}>
            <option value="fecha">Más recientes</option>
            <option value="estado">Por estado</option>
            <option value="pedido">Por pedido</option>
          </select>
          <button onClick={syncStates} disabled={syncing}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 8, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' }}>
            {syncing ? <Loader2 size={13} className="spin" /> : <Truck size={13} />} Refrescar estados
          </button>
        </span>
      </div>

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', padding: '16px 0' }}><Loader2 size={15} className="spin" /> Cargando envíos…</div>
      ) : !filtered.length ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '16px 0' }}>Sin envíos en este filtro.</div>
      ) : (
        <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
          {filtered.map((r) => {
            const info = orderInfo.get(r.order_id);
            const url = r.raw?.shipped?.parcels?.[0]?.tracking_url || r.raw?.tracking_url;
            const canWa = !r.raw?.wa_sent && !['created', 'ready'].includes(r.status);
            return (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                onClick={() => info && onOpenStatus(r.order_id, info.display)}
                title="Ver estado e historial">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{info?.display || r.external_id} · {info?.name || '—'}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                    {new Date(r.created_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })} · etiqueta {r.external_id}
                  </div>
                </div>
                <span style={{ fontSize: '0.76rem', fontWeight: 700, color: stateColor(r.status), whiteSpace: 'nowrap' }}>
                  {CABIFY_STATE_ES[r.status] || r.status}
                </span>
                {url && (
                  <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                    style={{ color: '#7c3aed', display: 'inline-flex' }} title="Seguimiento en vivo">
                    <ExternalLink size={14} />
                  </a>
                )}
                {!CABIFY_DEAD.includes(r.status) && (() => {
                  // hermanos = los demás bultos del MISMO pedido (misma order_id)
                  const hermanos = r.order_id
                    ? rows.filter(x => x.order_id === r.order_id && !CABIFY_DEAD.includes(x.status))
                    : [r];
                  const ids = hermanos.map(x => x.parcel_id);
                  return (
                  <button
                    onClick={(e) => { e.stopPropagation(); printOne(ids, r.parcel_id); }}
                    disabled={lblBusy === r.parcel_id}
                    title={ids.length > 1
                      ? `Imprimir las ${ids.length} etiquetas de este pedido (multi-bulto)`
                      : `Imprimir la etiqueta de ${r.external_id}`}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 7, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {lblBusy === r.parcel_id ? <Loader2 size={12} className="spin" /> : <Printer size={12} />}
                    {ids.length > 1 ? `Etiquetas (${ids.length})` : 'Etiqueta'}
                  </button>
                  );
                })()}
                {canWa ? (
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      const who = info ? `${info.display} (${info.name})` : r.external_id;
                      if (!window.confirm(`¿Mandar el WhatsApp de "pedido en camino" con el link de seguimiento a ${who}?`)) return;
                      setWaBusy(r.parcel_id);
                      try {
                        const { data, error } = await supabase.functions.invoke('cabify-logistics', { body: { action: 'send_wa', parcel_id: r.parcel_id } });
                        if (error || !data?.ok) window.alert(`No se pudo mandar: ${data?.error || error?.message || 'error'}`);
                        else await load();
                      } finally {
                        setWaBusy(null);
                      }
                    }}
                    disabled={waBusy === r.parcel_id}
                    title="Mandar aviso de WhatsApp con el link de seguimiento"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 7, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', border: 'none', background: '#25d366', color: '#fff', whiteSpace: 'nowrap' }}>
                    {waBusy === r.parcel_id ? <Loader2 size={12} className="spin" /> : <Send size={12} />} Aviso WA
                  </button>
                ) : r.raw?.wa_sent ? (
                  <span style={{ fontSize: '0.68rem', color: '#25d366', fontWeight: 700, whiteSpace: 'nowrap' }}>WA ✓</span>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}

// Config de bultos por producto: productos que viajan en más de un paquete
// (ej: Sun&Moon = 2 cajas → 2 etiquetas/parcels por unidad, se paga por bulto).
function CabifyBultosConfig() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [prods, setProds] = useState<any[]>([]);
  const [multi, setMulti] = useState<any[]>([]);

  const loadMulti = useCallback(async () => {
    const { data } = await supabase.from('tn_products')
      .select('id, name, cabify_bultos').gt('cabify_bultos', 1).order('name');
    setMulti(data || []);
  }, []);
  useEffect(() => { if (open) loadMulti(); }, [open, loadMulti]);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!q.trim()) { setProds([]); return; }
      const { data } = await supabase.from('tn_products')
        .select('id, name, cabify_bultos').eq('active', true).ilike('name', `%${q.trim()}%`).order('name').limit(10);
      setProds(data || []);
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const setBultos = async (id: number, n: number) => {
    const v = Math.min(Math.max(Math.round(n) || 1, 1), 8);
    await supabase.from('tn_products').update({ cabify_bultos: v }).eq('id', id);
    setProds(ps => ps.map(p => p.id === id ? { ...p, cabify_bultos: v } : p));
    loadMulti();
  };

  const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderTop: '1px solid var(--border)', fontSize: '0.83rem' };
  const numStyle: React.CSSProperties = { width: 56, textAlign: 'center', padding: '4px 6px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' };

  return (
    <div style={{ margin: '14px 0', border: '1px solid var(--border)', borderRadius: 12, background: 'var(--card)' }}>
      <button onClick={() => setOpen(!open)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text)', fontWeight: 700, fontSize: '0.85rem' }}>
        📦 Bultos por producto {multi.length > 0 && !open ? `(${multi.length} configurados)` : ''} <span style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 14px 14px' }}>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', margin: '0 0 8px' }}>
            Productos que viajan en más de un paquete: al generar etiquetas Cabify, cada unidad crea esa cantidad de etiquetas (una por bulto — se paga cada bulto). Ej: Lámpara Sun&Moon = 2.
          </p>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar producto para configurar…"
            style={{ width: '100%', maxWidth: 340, padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '0.83rem', marginBottom: 8 }} />
          {prods.map(p => (
            <div key={p.id} style={rowStyle}>
              <span style={{ flex: 1 }}>{p.name}</span>
              <input type="number" min={1} max={8} value={p.cabify_bultos || 1} onChange={e => setBultos(p.id, Number(e.target.value))} style={numStyle} />
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>bultos</span>
            </div>
          ))}
          {!q.trim() && multi.length > 0 && (
            <>
              <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', margin: '10px 0 4px' }}>Con más de 1 bulto</div>
              {multi.map(p => (
                <div key={p.id} style={rowStyle}>
                  <span style={{ flex: 1 }}>{p.name}</span>
                  <input type="number" min={1} max={8} value={p.cabify_bultos} onChange={e => setBultos(p.id, Number(e.target.value))} style={numStyle} />
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>bultos</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ===== Envío MANUAL (sin pedido) =====
// Para el día a día: mandar algo a alguien sin que haya una orden en el sistema.
// Cotiza los dos servicios de la cuenta (Express = moto al toque / Entrega en el mismo
// día, más barato) y con el elegido crea el paquete y lo despacha.
function CabifyManualShipment({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ nombre: '', tel: '', dir: '', piso: '', contenido: '', largo: '30', ancho: '30', alto: '30', peso: '2' });
  // lat/lon de la sugerencia elegida: con coordenadas Cabify no geocodifica el texto,
  // que es donde falla con direcciones sin localidad.
  const [coords, setCoords] = useState<{ lat: number; lon: number } | null>(null);
  const [quotes, setQuotes] = useState<any[] | null>(null);
  const [pick, setPick] = useState<string>('');
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<any | null>(null);

  const upd = (k: string, v: string) => setF(s => ({ ...s, [k]: v }));
  // true = servicio de ENTREGA EN PUNTO: el paquete lo llevás vos, no pasa el chofer.
  const esDropOff = (t: any) => /llev[aá]|punto|drop\s*-?off|sucursal/i.test(`${t?.name || ''} ${t?.description || ''}`);
  const specs = () => ({ width: Number(f.ancho) || 0, height: Number(f.alto) || 0, length: Number(f.largo) || 0, weight_kg: Number(f.peso) || 0 });
  const money = (cents?: number) => cents == null ? '—' : '$' + Math.round(cents / 100).toLocaleString('es-AR');
  const hora = (iso?: string) => {
    if (!iso || iso.startsWith('0001')) return null;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  };

  // La dirección TIENE que traer localidad. El estimate cotiza igual sin ella, pero el
  // create de Cabify rebota con un 404 "not found" — así que sin esto el form muestra
  // precios y recién explota al confirmar (caso "Franklin D. Roosevelt 2891", 30/09).
  const faltaLocalidad = (d: string) => {
    const segs = d.split(',').map(x => x.trim()).filter(Boolean);
    if (segs.length >= 2) return false;
    return !/\b(caba|capital federal|ciudad de buenos aires|buenos aires|argentina)\b/i.test(d);
  };

  const cotizar = async () => {
    if (!coords && faltaLocalidad(f.dir.trim())) {
      setErr('Agregá la localidad después de la calle y altura: "Franklin D. Roosevelt 2891, CABA" o "Av. Maipú 3820, Vicente López". Sin localidad, Cabify cotiza pero después no puede crear la etiqueta.');
      return;
    }
    if (!f.dir.trim()) { setErr('Poné la dirección de entrega.'); return; }
    // Sin coordenadas (texto tipeado, no una sugerencia): la ubicamos con Google y validamos
    // CP/altura. A Cabify nunca le mandamos solo texto (caso #4650, src/geocode.ts).
    if (!coords) {
      setBusy('quote'); setErr(null);
      const chk = await verifyAddress(f.dir.trim());
      setBusy(null);
      if (!chk.ok) {
        setErr(`${chk.reason}${chk.found ? `. Google la entendió como: ${chk.found}` : ''}. Elegí la dirección de las sugerencias del autocompletado.`);
        return;
      }
      setCoords({ lat: chk.lat!, lon: chk.lon! });
    }
    setBusy('quote'); setErr(null); setQuotes(null); setDone(null);
    try {
      const { data: st, error: e1 } = await supabase.functions.invoke('cabify-logistics', { body: { action: 'shipping_types' } });
      if (e1 || !st?.ok) throw new Error(st?.error || e1?.message || 'No pude traer los servicios');
      // "Envíos de comida" no aplica a paquetes
      const tipos = (st.shipping_types || []).filter((t: any) => !/comida|food/i.test(t.name || ''));
      const res = await Promise.all(tipos.map(async (t: any) => {
        const { data } = await supabase.functions.invoke('cabify-logistics', {
          body: {
            action: 'estimate', dropoff_addr: f.dir.trim(), shipping_type_id: t.id, specs: specs(),
            ...(coords ? { dropoff_lat: coords.lat, dropoff_lon: coords.lon } : {}),
          },
        });
        return { ...t, ok: !!data?.ok, est: data?.estimation || null, error: data?.error || null, resolved: data?.resolved_addr, note: data?.addr_note };
      }));
      const validas = res.filter(r => r.ok);
      if (!validas.length) throw new Error(res[0]?.error || 'Cabify no pudo cotizar esa dirección');
      validas.sort((a, b) => ((a.est?.price?.amount ?? 9e9) - (b.est?.price?.amount ?? 9e9)));
      setQuotes(validas);
      setPick(validas[0].id);
    } catch (e) { setErr(String((e as Error).message || e)); }
    finally { setBusy(null); }
  };

  const pedir = async () => {
    const q = quotes?.find(x => x.id === pick);
    if (!q) return;
    const aviso = esDropOff(q)
      ? `📍 "${q.name}": lo llevás vos a un punto Cabify. OJO: el envío NO arranca al crear la etiqueta, arranca cuando el punto lo escanea. Costo ${money(q.est?.price?.amount)}. ¿Confirmás?`
      : `⚠️ Esto pide un chofer de Cabify DE VERDAD y tiene costo (${money(q.est?.price?.amount)} — ${q.name}). ¿Confirmás?`;
    if (!window.confirm(aviso)) return;
    setBusy('ship'); setErr(null);
    try {
      const now = new Date();
      const display = `M-${String(now.getDate()).padStart(2, '0')}${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      const instr = [f.piso.trim() ? `Piso/depto: ${f.piso.trim()}` : '', f.contenido.trim() ? `Contenido: ${f.contenido.trim()}` : ''].filter(Boolean).join(' · ');
      const { data, error } = await supabase.functions.invoke('cabify-logistics', {
        body: {
          action: 'create_and_ship', shipping_type_id: pick, order_display: display,
          dropoff_addr: f.dir.trim(), dropoff_name: f.nombre.trim() || 'Destinatario',
          ...(coords ? { dropoff_lat: coords.lat, dropoff_lon: coords.lon } : {}),
          dropoff_phone: f.tel.trim(), instr, specs: specs(),
        },
      });
      if (error || !data?.ok) throw new Error(data?.error || error?.message || 'No se pudo despachar');
      setDone({ ...data, display });
      setQuotes(null);
      onDone();
    } catch (e) { setErr(String((e as Error).message || e)); }
    finally { setBusy(null); }
  };

  const inp: React.CSSProperties = { width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)', fontSize: '0.85rem' };
  const lab: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em' };

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 12, marginBottom: 14, background: 'var(--bg-card)' }}>
      <button onClick={() => setOpen(!open)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text)', fontSize: '0.9rem', fontWeight: 700 }}>
        <Send size={16} color="#7c3aed" /> Nuevo envío manual (sin pedido)
        <span style={{ marginLeft: 'auto', fontSize: '0.75rem', fontWeight: 400, color: 'var(--text-muted)' }}>{open ? '−' : '+'}</span>
      </button>
      {open && (
        <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
            <label style={lab}>Nombre<input style={inp} value={f.nombre} onChange={e => upd('nombre', e.target.value)} placeholder="Quién recibe" /></label>
            <label style={lab}>Teléfono<input style={inp} value={f.tel} onChange={e => upd('tel', e.target.value)} placeholder="11 5555 5555" /></label>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: 8 }}>
            <label style={lab}>
              Dirección <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(elegí una sugerencia de Google)</span>
              {/* Mismo campo que en la carga de órdenes: autocompleta con Google y guarda
                  lat/lon, así Cabify no tiene que interpretar el texto. El botón Verificar
                  no va acá porque este formulario ya cotiza al apretar "Cotizar". */}
              <AddressField value={f.dir} onChange={v => upd('dir', v)} onCoords={setCoords} showVerify={false}
                placeholder="Av. Santa Fe 3253, CABA" />
            </label>
            <label style={lab}>Piso/depto<input style={inp} value={f.piso} onChange={e => upd('piso', e.target.value)} placeholder="4B" /></label>
          </div>
          <label style={lab}>Contenido (va en la etiqueta y como nota al chofer)
            <input style={inp} value={f.contenido} onChange={e => upd('contenido', e.target.value)} placeholder="Ej: repuesto tensor / muestra de acrílico" /></label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
            <label style={lab}>Largo cm<input type="number" style={inp} value={f.largo} onChange={e => upd('largo', e.target.value)} /></label>
            <label style={lab}>Ancho cm<input type="number" style={inp} value={f.ancho} onChange={e => upd('ancho', e.target.value)} /></label>
            <label style={lab}>Alto cm<input type="number" style={inp} value={f.alto} onChange={e => upd('alto', e.target.value)} /></label>
            <label style={lab}>Peso kg<input type="number" step="0.1" style={inp} value={f.peso} onChange={e => upd('peso', e.target.value)} /></label>
          </div>

          <button onClick={cotizar} disabled={busy === 'quote'}
            style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', opacity: busy === 'quote' ? 0.6 : 1 }}>
            {busy === 'quote' ? <Loader2 size={14} className="spin" /> : <Truck size={14} />} Cotizar
          </button>

          {err && <div style={{ background: 'rgba(220,38,38,0.1)', color: '#dc2626', padding: '8px 10px', borderRadius: 8, fontSize: '0.8rem' }}>{err}</div>}

          {quotes && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)' }}>Elegí el servicio:</div>
              {quotes.map(q => {
                const sel = q.id === pick;
                const hp = hora(q.est?.eta_to_pickup), hd = hora(q.est?.eta_to_delivery);
                return (
                  <label key={q.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, cursor: 'pointer', border: `2px solid ${sel ? '#7c3aed' : 'var(--border)'}`, background: sel ? 'rgba(124,58,237,0.06)' : 'transparent' }}>
                    <input type="radio" checked={sel} onChange={() => setPick(q.id)} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>
                        {q.name}
                        {esDropOff(q) && (
                          <span style={{ marginLeft: 6, background: '#d97706', color: '#fff', borderRadius: 6, padding: '1px 6px', fontSize: '0.68rem', fontWeight: 800 }}>
                            LO LLEVÁS VOS
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.74rem', color: esDropOff(q) ? '#d97706' : 'var(--text-muted)' }}>
                        {esDropOff(q)
                          ? '📍 Lo llevás a un punto Cabify — el envío arranca cuando el punto lo escanea, no al crear la etiqueta'
                          : (q.est?.asset_kind === 'moped' ? '🛵 moto — pasa a buscarlo' : q.modality === 'same_day' ? '📦 en el día — pasa a buscarlo' : '🚗 express — pasa a buscarlo')}
                        {hp && ` · retira ${hp}`}{hd && ` · entrega ${hd}`}
                      </div>
                    </div>
                    <div style={{ fontWeight: 800, fontSize: '1rem' }}>{money(q.est?.price?.amount)}</div>
                  </label>
                );
              })}
              {quotes[0]?.note && <div style={{ fontSize: '0.72rem', color: '#d97706' }}>{quotes[0].note}</div>}
              <button onClick={pedir} disabled={busy === 'ship' || !pick}
                style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', opacity: busy === 'ship' ? 0.6 : 1 }}>
                {busy === 'ship' ? <Loader2 size={14} className="spin" /> : <Send size={14} />} Confirmar y pedir
              </button>
            </div>
          )}

          {done && (
            <div style={{ background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.3)', borderRadius: 10, padding: '10px 12px', fontSize: '0.85rem' }}>
              <div style={{ fontWeight: 700, color: '#16a34a', marginBottom: 4 }}>✓ Envío despachado — {done.display}</div>
              {done.tracking_url && (
                <a href={done.tracking_url} target="_blank" rel="noreferrer" style={{ color: '#7c3aed', fontSize: '0.8rem' }}>Seguimiento en vivo →</a>
              )}
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                Ya aparece en la lista de abajo; desde ahí imprimís la etiqueta.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Pantalla completa de envíos Cabify (tab /cabify): la misma lista, a página entera.
export function CabifyScreen() {
  const [statusFor, setStatusFor] = useState<{ orderId: number; displayId: string } | null>(null);
  const [reload, setReload] = useState(0);
  return (
    <div style={{ padding: '16px 20px' }}>
      <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 4px', fontSize: '1.25rem' }}>
        <Truck size={22} color="#7c3aed" /> Envíos Cabify
      </h2>
      <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0 0 16px' }}>
        Todos los envíos con su estado en vivo (los actualiza el webhook de Cabify). Click en un envío para ver el historial completo.
      </p>
      <CabifyLate key={`late-${reload}`} />
      <CabifyControl key={`ctl-${reload}`} />
      <div style={{ maxWidth: 900 }}>
      <CabifyManualShipment onDone={() => setReload(r => r + 1)} />
      <CabifyBultosConfig />
      <CabifyShipments key={reload} onOpenStatus={(orderId, displayId) => setStatusFor({ orderId, displayId })} />
      </div>
      {statusFor && (
        <CabifyStatusModal orderId={statusFor.orderId} displayId={statusFor.displayId} onClose={() => setStatusFor(null)} />
      )}
    </div>
  );
}

export default function CabifyBatch({ orders, userEmail, onClose }: { orders: CabifyBatchOrder[]; userEmail?: string; onClose: () => void }) {
  const [deliveries, setDeliveries] = useState<Map<number, DeliveryRow[]>>(new Map());
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<string | null>(null); // 'create' | 'labels' | 'ship'
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [view, setView] = useState<'toShip' | 'shipments'>('toShip');
  const [statusFor, setStatusFor] = useState<{ orderId: number; displayId: string } | null>(null);
  // Verificación de ubicación por pedido (src/geocode.ts): solo se despacha lo verificado.
  const [geo, setGeo] = useState<Map<number, AddressCheck | 'checking'>>(new Map());
  const [fixing, setFixing] = useState<number | null>(null);
  const [fixAddr, setFixAddr] = useState('');
  // AddressField avisa las coordenadas en el MISMO handler en que cambia el texto: el estado
  // todavía no se actualizó, así que el último texto se lee de una ref.
  const fixAddrRef = React.useRef('');
  const onFixAddr = (v: string) => { fixAddrRef.current = v; setFixAddr(v); };
  useEffect(() => {
    let alive = true;
    (async () => {
      for (const o of orders) {
        if (!o.delivery_address) continue;
        setGeo(m => new Map(m).set(o.id, 'checking'));
        const r = await ensureOrderCoords(o as any);
        if (!alive) return;
        setGeo(m => new Map(m).set(o.id, r));
        if (!r.ok) setChecked(c => { const n = new Set(c); n.delete(o.id); return n; });
      }
    })();
    return () => { alive = false; };
  }, [orders]);
  const geoOk = (id: number) => { const g = geo.get(id); return !!g && g !== 'checking' && g.ok; };
  // Corrección en el momento: elegir una sugerencia del autocompletado trae coordenadas exactas.
  const saveFix = async (o: CabifyBatchOrder, c: { lat: number; lon: number }) => {
    const addr = fixAddrRef.current || fixAddr;
    await supabase.from('orders').update({ delivery_address: addr, delivery_lat: c.lat, delivery_lon: c.lon }).eq('id', o.id);
    o.delivery_address = addr; o.delivery_lat = c.lat; o.delivery_lon = c.lon;
    setGeo(m => new Map(m).set(o.id, { ok: true, lat: c.lat, lon: c.lon, found: addr }));
    setChecked(cs => new Set(cs).add(o.id));
    setFixing(null);
  };

  const loadDeliveries = useCallback(async () => {
    if (!orders.length) return;
    const { data } = await supabase.from('cabify_deliveries')
      .select('order_id, parcel_id, status, raw')
      .in('order_id', orders.map((o) => o.id))
      .not('status', 'in', `(${CABIFY_DEAD.join(',')})`)
      .order('created_at', { ascending: true });
    const map = new Map<number, DeliveryRow[]>();
    (data || []).forEach((d: any) => {
      const arr = map.get(d.order_id) || [];
      arr.push(d);
      map.set(d.order_id, arr);
    });
    setDeliveries(map);
    // por defecto: tildar los que todavía no tienen envío creado
    setChecked(new Set(orders.filter((o) => !map.has(o.id) && o.delivery_address).map((o) => o.id)));
  }, [orders]);

  useEffect(() => { loadDeliveries(); }, [loadDeliveries]);

  const createdRows = useMemo(() => Array.from(deliveries.values()).flat().filter((d) => d.status === 'created'), [deliveries]);
  // solo lo que tiene la ubicación verificada (nunca se le manda a Cabify texto solo)
  const toCreate = useMemo(() => orders.filter((o) => checked.has(o.id) && !deliveries.has(o.id) && geoOk(o.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orders, checked, deliveries, geo]);

  const openPdf = (b64: string) => {
    const bytes = atob(b64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const url = URL.createObjectURL(new Blob([arr], { type: 'application/pdf' }));
    window.open(url, '_blank');
  };

  const fetchLabels = async (parcelIds: string[]) => {
    const { data, error } = await supabase.functions.invoke('cabify-logistics', {
      body: { action: 'labels', parcel_ids: parcelIds },
    });
    if (error || !data?.ok) throw new Error(data?.error || error?.message || 'No se pudieron bajar las etiquetas');
    openPdf(data.pdf_base64);
    return data.pages as number;
  };

  const createAndLabel = async () => {
    setBusy('create'); setErr(null); setMsg(null);
    try {
      let createdIds: string[] = [];
      if (toCreate.length) {
        const { data, error } = await supabase.functions.invoke('cabify-logistics', {
          body: {
            action: 'create_batch',
            created_by: userEmail,
            orders: toCreate.map((o) => ({
              order_id: o.id, order_display: o.displayId, dropoff_addr: o.delivery_address,
              dropoff_lat: (geo.get(o.id) as AddressCheck).lat, dropoff_lon: (geo.get(o.id) as AddressCheck).lon,
              dropoff_name: o.contact_name, dropoff_phone: o.contact_phone || (o as any).canje_phone,
              // piso/depto va como indicación al chofer (la dirección queda limpia para el mapa)
              instr: o.delivery_unit ? `Piso/depto: ${o.delivery_unit}` : undefined,
            })),
          },
        });
        if (error || !data?.ok) throw new Error(data?.error || error?.message || 'Falló la creación');
        const fails = (data.results || []).filter((r: any) => !r.ok);
        createdIds = (data.results || []).filter((r: any) => r.ok).flatMap((r: any) => r.parcel_ids || [r.parcel_id]);
        if (fails.length) setErr(`No se pudo crear el envío de: ${fails.map((f: any) => `${orders.find((o) => o.id === f.order_id)?.displayId || f.order_id} (${f.error})`).join(' · ')}`);
      }
      await loadDeliveries();
      const allIds = Array.from(new Set([...createdRows.map((d) => d.parcel_id), ...createdIds]));
      if (allIds.length) {
        const pages = await fetchLabels(allIds);
        setMsg(`${allIds.length} envío(s) listos — PDF con ${pages} etiqueta(s) abierto para imprimir.`);
      } else if (!toCreate.length) {
        setErr('No hay pedidos tildados sin envío creado.');
      }
    } catch (e) {
      setErr(String((e as Error).message || e));
    } finally {
      setBusy(null);
    }
  };

  const reprintLabels = async () => {
    setBusy('labels'); setErr(null); setMsg(null);
    try {
      if (!createdRows.length) { setErr('No hay envíos creados sin despachar.'); return; }
      const pages = await fetchLabels(createdRows.map((d) => d.parcel_id));
      setMsg(`PDF con ${pages} etiqueta(s) abierto.`);
    } catch (e) {
      setErr(String((e as Error).message || e));
    } finally {
      setBusy(null);
    }
  };

  const shipAll = async () => {
    if (!createdRows.length) return;
    if (!window.confirm(`⚠️ Esto DESPACHA ${createdRows.length} envío(s) con choferes de Cabify de verdad y tiene costo. ¿Confirmás?`)) return;
    setBusy('ship'); setErr(null); setMsg(null);
    try {
      const { data, error } = await supabase.functions.invoke('cabify-logistics', {
        body: { action: 'ship_batch', parcel_ids: createdRows.map((d) => d.parcel_id) },
      });
      if (error || !data?.ok) throw new Error(data?.error || error?.message || 'Falló el despacho');
      setMsg(`🛵 ${data.shipped?.length || 0} envío(s) despachados. Cabify va a pasar a retirarlos.`);
      await loadDeliveries();
    } catch (e) {
      setErr(String((e as Error).message || e));
    } finally {
      setBusy(null);
    }
  };

  const ov: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
  const card: React.CSSProperties = { background: 'var(--card)', borderRadius: 16, width: '100%', maxWidth: 640, padding: 20, maxHeight: '90vh', overflowY: 'auto', color: 'var(--text)' };
  const btn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 9, fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)' };

  const chip = (ds?: DeliveryRow[]) => {
    if (!ds?.length) return <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>sin envío</span>;
    const n = ds.length;
    const d = ds[0];
    if (ds.every((x) => x.status === 'created')) {
      return <span style={{ fontSize: '0.72rem', color: '#7c3aed', fontWeight: 600 }}>{n > 1 ? `${n} etiquetas listas` : 'etiqueta lista'}</span>;
    }
    const url = d.raw?.shipped?.parcels?.[0]?.tracking_url || d.raw?.tracking_url;
    const color = ds.every((x) => x.status === 'delivered') ? '#10b981' : '#7c3aed';
    return (
      <span style={{ fontSize: '0.72rem', color, fontWeight: 600 }}>
        {CABIFY_STATE_ES[d.status] || d.status}{n > 1 ? ` (×${n})` : ''}
        {url && <> · <a href={url} target="_blank" rel="noopener noreferrer" style={{ color }}>seguimiento <ExternalLink size={10} /></a></>}
      </span>
    );
  };

  return (
    <div style={ov} onClick={onClose}>
      <div style={card} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, fontSize: '1.1rem' }}>
            <Truck size={20} color="#7c3aed" /> Cabify
          </h3>
          <button style={{ ...btn, border: 'none', background: 'transparent', padding: 6 }} onClick={onClose}><X size={20} /></button>
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
          {([['toShip', 'Para despachar'], ['shipments', 'Envíos']] as const).map(([k, label]) => (
            <button key={k} onClick={() => setView(k)}
              style={{ padding: '6px 14px', borderRadius: 8, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', border: '1px solid var(--border)', background: view === k ? '#7c3aed' : 'var(--bg)', color: view === k ? '#fff' : 'var(--text)' }}>
              {label}
            </button>
          ))}
        </div>

        {view === 'shipments' ? (
          <CabifyShipments onOpenStatus={(orderId, displayId) => setStatusFor({ orderId, displayId })} />
        ) : (<>
        <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '0 0 14px' }}>
          Pedidos <b>empaquetados</b> con logística propia / envío gratis / manuales. Flujo: crear envíos → imprimir etiquetas → pegar → despachar todo junto.
        </p>

        {orders.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem', padding: '20px 0' }}>No hay pedidos empaquetados calificados para Cabify.</div>
        ) : (
          <div style={{ border: '1px solid var(--border)', borderRadius: 12, marginBottom: 14, overflow: 'hidden' }}>
            {orders.map((o) => {
              const d = deliveries.get(o.id);
              return (
                <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderBottom: '1px solid var(--border)', cursor: d ? 'default' : 'pointer', opacity: o.delivery_address ? 1 : 0.5 }}>
                  <input
                    type="checkbox"
                    checked={checked.has(o.id)}
                    disabled={!!d || !o.delivery_address || !geoOk(o.id)}
                    onChange={() => {
                      const next = new Set(checked);
                      if (next.has(o.id)) next.delete(o.id); else next.add(o.id);
                      setChecked(next);
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{o.displayId} · {o.contact_name || 'Sin nombre'}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {o.delivery_address || '⚠️ sin dirección'}{o.delivery_unit ? ` · Piso/Depto ${o.delivery_unit}` : ''}
                    </div>
                    {!d && o.delivery_address && (() => {
                      const g = geo.get(o.id);
                      if (!g || g === 'checking') return <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>verificando ubicación…</div>;
                      if (g.ok) return <div style={{ fontSize: '0.7rem', color: '#16a34a' }} title={g.found}>📍 Ubicación verificada{g.found ? ` · ${g.found}` : ''}</div>;
                      return (
                        <div style={{ fontSize: '0.72rem', color: '#dc2626', marginTop: 2 }} onClick={(e) => e.preventDefault()}>
                          ⚠️ No se puede despachar: {g.reason}{g.found ? <> · Google: <i>{g.found}</i></> : null}
                          {fixing === o.id ? (
                            <div style={{ marginTop: 6 }}>
                              <AddressField value={fixAddr} onChange={onFixAddr} showVerify={false}
                                onCoords={(c) => { if (c) saveFix(o, c); }}
                                placeholder="Escribí la dirección y elegí la sugerencia correcta" />
                            </div>
                          ) : (
                            <button type="button" onClick={() => { setFixing(o.id); onFixAddr(o.delivery_address || ''); }}
                              style={{ marginLeft: 6, fontSize: '0.72rem', fontWeight: 700, color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                              Corregir dirección
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                  {chip(d)}
                </label>
              );
            })}
          </div>
        )}

        {err && <div style={{ color: 'var(--danger, #dc2626)', fontSize: '0.83rem', marginBottom: 10 }}>⚠️ {err}</div>}
        {msg && <div style={{ color: '#10b981', fontSize: '0.83rem', marginBottom: 10 }}>{msg}</div>}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={{ ...btn, background: '#7c3aed', color: '#fff', border: 'none' }} onClick={createAndLabel} disabled={!!busy || (!toCreate.length && !createdRows.length)}>
            {busy === 'create' ? <Loader2 size={14} className="spin" /> : <Printer size={14} />}
            Crear envíos + etiquetas{toCreate.length ? ` (${toCreate.length})` : ''}
          </button>
          <button style={btn} onClick={reprintLabels} disabled={!!busy || !createdRows.length}>
            {busy === 'labels' ? <Loader2 size={14} className="spin" /> : <Printer size={14} />}
            Reimprimir etiquetas ({createdRows.length})
          </button>
          <button style={{ ...btn, background: '#10b981', color: '#fff', border: 'none', marginLeft: 'auto' }} onClick={shipAll} disabled={!!busy || !createdRows.length}>
            {busy === 'ship' ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
            Despachar todos ({createdRows.length})
          </button>
        </div>

        <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 14 }}>
          "Crear envíos" registra los paquetes en Cabify y abre un PDF con todas las etiquetas (el chofer las escanea al retirar/entregar). No cuesta nada hasta tocar <b>Despachar</b>, que envía choferes reales y tiene costo.
        </p>
        </>)}
      </div>
      {statusFor && (
        <CabifyStatusModal orderId={statusFor.orderId} displayId={statusFor.displayId} onClose={() => setStatusFor(null)} />
      )}
    </div>
  );
}
